import { motion, AnimatePresence } from 'framer-motion';
import { Clock, RefreshCw } from 'lucide-react';
import AnimatedFlightList from './AnimatedFlightList';
import type { ClaimState, ClaimPhase, FlightSegment, IncidentType, Expense, UploadedDocument, WsStatus, ExtractionProgress } from './types';
import type { ClaimTheme } from './constants';
import { CanvasBackground } from './canvas/CanvasBackground';
import { PassengerChips } from './canvas/PassengerChips';
import { SuccessCelebration } from './canvas/SuccessCelebration';

// New canvas section components
import { WelcomeSection } from './canvas/WelcomeSection';
import { ExtractingSection } from './canvas/ExtractingSection';
import { AwaitingEmailSection } from './canvas/AwaitingEmailSection';
import { BookingFoundSection } from './canvas/BookingFoundSection';
import { IncidentSelectSection } from './canvas/IncidentSelectSection';
import { IncidentDetailsSection } from './canvas/IncidentDetailsSection';
import { DocumentationSection } from './canvas/DocumentationSection';
import { ExpensesSection } from './canvas/ExpensesSection';
import { NarrativeSection } from './canvas/NarrativeSection';
import { ReviewSection } from './canvas/ReviewSection';
import { SubmittingSection } from './canvas/SubmittingSection';

// Token expiry overlay component
function TokenExpiredOverlay() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-2xl shadow-2xl p-8 max-w-md mx-4 text-center"
      >
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
          <Clock className="w-8 h-8 text-amber-600" />
        </div>
        <h3 className="text-xl font-semibold text-slate-900 mb-2">Sitzung abgelaufen</h3>
        <p className="text-slate-600 mb-6">
          Die E-Mail-Adresse ist abgelaufen. Bitte laden Sie die Seite neu, um eine neue zu erhalten.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 text-white rounded-xl font-medium hover:bg-amber-600 transition-colors shadow-lg shadow-amber-500/25"
        >
          <RefreshCw className="w-4 h-4" />
          Seite neu laden
        </button>
      </motion.div>
    </motion.div>
  );
}

interface CanvasProps {
  state: ClaimState;
  theme?: ClaimTheme;
  wsStatus: WsStatus;
  reconnectAttempt?: number;
  onRetryConnection?: () => void;
  extractionProgress: ExtractionProgress | null;

  // Email notification state (for awaiting_email phase)
  emailNotificationState?: {
    emailReceived: boolean;
    emailSubject: string | null;
    emailFrom: string | null;
    isExtracting: boolean;
    error: string | null;
    errorDetails: {
      reason: string;
      attachmentsFound: Array<{
        filename: string;
        category: string;
        type: string;
      }>;
    } | null;
  };

  // Segment editing
  onUpdateSegment: (id: string, field: keyof FlightSegment, value: string) => void;
  onDeleteSegment: (id: string) => void;
  onAddSegment: () => void;

  // Passenger editing
  onUpdatePassenger: (id: string, field: 'first_name' | 'last_name', value: string) => void;
  onDeletePassenger: (id: string) => void;
  onAddPassenger: () => void;

  // Navigation
  onContinue: () => void;
  onBack: () => void;
  onSkip: () => void;
  onReupload: () => void;

  // File upload
  onFileUpload: (files: File[]) => void;

  // Email forwarding
  onUseEmailForwarding: () => void;

  // Incident
  onSelectIncidentType: (type: IncidentType) => void;
  onDescriptionChange: (desc: string) => void;
  onSelectAffectedSegment: (id: string) => void;

  // Documents
  onUploadDocument: (file: File, forRequirement?: string) => void;
  onRemoveDocument: (id: string) => void;

  // Expenses
  onAddExpense: (expense: Omit<Expense, 'id' | '_isNew'>) => void;
  onRemoveExpense: (id: string) => void;

  // Narrative
  onNarrativeChange: (text: string) => void;

  // Submit & actions
  onSubmit: () => void;
  onEditSection: (section: string) => void;
  onViewCase: () => void;
  onBackToOverview: () => void;
  onStartFresh: () => void;
}

// Layout modes
type LayoutMode = 'centered' | 'split' | 'full';

function getLayoutMode(phase: ClaimPhase): LayoutMode {
  switch (phase) {
    case 'welcome':
    case 'extracting':
    case 'awaiting_email':
    case 'submitting':
    case 'complete':
      return 'centered';
    case 'booking_review':
    case 'incident_intro':
    case 'incident_select':
    case 'incident_details':
    case 'evidence_intro':
    case 'documentation':
    case 'expenses':
    case 'narrative':
      return 'split';
    case 'review':
      return 'full';
    default:
      return 'centered';
  }
}

// Check if we should show compact flights (after booking_review)
function shouldShowCompactFlights(phase: ClaimPhase): boolean {
  return [
    'incident_intro',
    'incident_select',
    'incident_details',
    'evidence_intro',
    'documentation',
    'expenses',
    'narrative',
  ].includes(phase);
}

export default function Canvas({
  state,
  theme = 'invol',
  wsStatus,
  reconnectAttempt = 0,
  onRetryConnection,
  extractionProgress,
  emailNotificationState,
  onUpdateSegment,
  onDeleteSegment,
  onAddSegment,
  onUpdatePassenger,
  onDeletePassenger,
  onAddPassenger,
  onContinue,
  onBack,
  onSkip,
  onReupload,
  onFileUpload,
  onUseEmailForwarding,
  onSelectIncidentType,
  onDescriptionChange,
  onSelectAffectedSegment,
  onUploadDocument,
  onRemoveDocument,
  onAddExpense,
  onRemoveExpense,
  onNarrativeChange,
  onSubmit,
  onEditSection,
  onViewCase,
  onBackToOverview,
  onStartFresh,
}: CanvasProps) {
  const { phase, editableSegments, passengers, pnr, bookingDate, tokenExpired, caseId } = state;
  const hasData = editableSegments.length > 0;
  const layoutMode = getLayoutMode(phase);
  const showCompactFlights = shouldShowCompactFlights(phase);
  const isCompletePhase = phase === 'complete';

  // Show token expiry overlay in welcome/awaiting_email phases when no case exists
  const showTokenExpiry = tokenExpired && !caseId && (phase === 'welcome' || phase === 'awaiting_email');

  // Render the right-side content based on phase
  const renderRightContent = () => {
    switch (phase) {
      case 'booking_review':
        return (
          <BookingFoundSection
            flightCount={editableSegments.filter(s => !s._isDeleted).length}
            passengerCount={passengers.length}
            onContinue={onContinue}
            onReupload={onReupload}
            onStartFresh={onStartFresh}
            theme={theme}
          />
        );

      case 'incident_intro':
      case 'incident_select':
        return (
          <IncidentSelectSection
            selectedType={state.incidentType}
            onSelect={(type) => {
              onSelectIncidentType(type);
              // Auto-advance to details after selection
              setTimeout(onContinue, 300);
            }}
            onBack={onBack}
            theme={theme}
          />
        );

      case 'incident_details':
        return (
          <IncidentDetailsSection
            incidentType={state.incidentType!}
            description={state.incidentDescription}
            affectedSegmentId={state.affectedSegmentId}
            segments={editableSegments}
            onDescriptionChange={onDescriptionChange}
            onContinue={onContinue}
            onBack={onBack}
            theme={theme}
          />
        );

      case 'evidence_intro':
      case 'documentation':
        return (
          <DocumentationSection
            documents={state.documents}
            incidentType={state.incidentType}
            onUpload={onUploadDocument}
            onRemove={onRemoveDocument}
            onContinue={onContinue}
            onSkip={onSkip}
            onBack={onBack}
            theme={theme}
          />
        );

      case 'expenses':
        return (
          <ExpensesSection
            expenses={state.expenses}
            onAddExpense={onAddExpense}
            onRemoveExpense={onRemoveExpense}
            onContinue={onContinue}
            onSkip={onSkip}
            onBack={onBack}
            caseId={state.caseId}
            theme={theme}
          />
        );

      case 'narrative':
        return (
          <NarrativeSection
            narrative={state.narrative}
            incidentType={state.incidentType}
            onChange={onNarrativeChange}
            onContinue={onContinue}
            onSkip={onSkip}
            onBack={onBack}
            theme={theme}
          />
        );

      default:
        return null;
    }
  };

  return (
    <motion.div
      className="min-h-screen relative"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      {/* Dynamic background */}
      <CanvasBackground phase={phase} theme={theme} />

      <div className="relative z-10 container mx-auto px-4 py-8 max-w-6xl">
        <AnimatePresence mode="wait">
          {/* ========== CENTERED LAYOUT ========== */}
          {layoutMode === 'centered' && (
            <motion.div
              key={`centered-${phase}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              {phase === 'welcome' && (
                <WelcomeSection
                  onFileUpload={onFileUpload}
                  onUseEmailForwarding={onUseEmailForwarding}
                  wsStatus={wsStatus}
                  reconnectAttempt={reconnectAttempt}
                  onRetry={onRetryConnection}
                  emailForwardingState={{
                    isLoading: !state.inboundEmailAddress,
                    emailAddress: state.inboundEmailAddress,
                    error: null,
                  }}
                  theme={theme}
                />
              )}

              {phase === 'extracting' && <ExtractingSection />}

              {phase === 'awaiting_email' && state.inboundEmailAddress && (
                <AwaitingEmailSection
                  emailAddress={state.inboundEmailAddress}
                  emailReceived={emailNotificationState?.emailReceived ?? false}
                  emailSubject={emailNotificationState?.emailSubject ?? null}
                  emailFrom={emailNotificationState?.emailFrom ?? null}
                  isExtracting={emailNotificationState?.isExtracting ?? false}
                  error={emailNotificationState?.error ?? null}
                  errorDetails={emailNotificationState?.errorDetails ?? null}
                  onBack={onBack}
                />
              )}

              {phase === 'submitting' && <SubmittingSection />}

              {phase === 'complete' && (
                <SuccessCelebration
                  caseNumber={state.caseNumber || 'XXXXXX'}
                  onViewCase={onViewCase}
                  onBackToOverview={onBackToOverview}
                  theme={theme}
                />
              )}
            </motion.div>
          )}

          {/* ========== SPLIT LAYOUT (Flights Left, Content Right) ========== */}
          {layoutMode === 'split' && hasData && (
            <motion.div
              key="split"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-8 min-h-[80vh]"
            >
              {/* Left side - Flight cards (no re-animation on phase change) */}
              <div className="lg:col-span-1">
                <div className="sticky top-8">
                  {/* PNR and booking date badges */}
                  <div className="flex items-center gap-3 mb-4 flex-wrap">
                    {pnr && (
                      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white shadow-sm border border-slate-100">
                        <span className="text-xs text-slate-400 uppercase tracking-wider">PNR</span>
                        <span className="font-mono text-sm font-semibold text-slate-900">{pnr}</span>
                      </div>
                    )}
                  </div>

                  {/* Flight cards - Animated between full and compact modes */}
                  <AnimatedFlightList
                    segments={editableSegments}
                    mode={showCompactFlights ? 'compact' : 'full'}
                    affectedSegmentId={state.affectedSegmentId}
                    incidentType={state.incidentType}
                    onUpdateSegment={onUpdateSegment}
                    onDeleteSegment={onDeleteSegment}
                    onAddSegment={onAddSegment}
                    onSegmentClick={onSelectAffectedSegment}
                  />

                  {/* Passenger chips */}
                  {passengers.length > 0 && (
                    <div className="mt-6">
                      <PassengerChips
                        passengers={passengers}
                        editable={!showCompactFlights}
                        compact={showCompactFlights}
                        onAdd={onAddPassenger}
                        onRemove={onDeletePassenger}
                        onUpdate={onUpdatePassenger}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Right side - Phase-specific content */}
              <div className="lg:col-span-1 relative pt-8">
                <AnimatePresence mode="wait">
                  {renderRightContent()}
                </AnimatePresence>
              </div>
            </motion.div>
          )}

          {/* ========== FULL LAYOUT (Review) ========== */}
          {layoutMode === 'full' && (
            <motion.div
              key="full-review"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <ReviewSection
                state={state}
                onSubmit={onSubmit}
                onBack={onBack}
                onEditSection={onEditSection}
                theme={theme}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Token expiry overlay */}
      <AnimatePresence>
        {showTokenExpiry && <TokenExpiredOverlay />}
      </AnimatePresence>
    </motion.div>
  );
}
